/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslate } from '@tolgee/react';
import { useSWRInfinite } from 'swr';
import { useCallback, useState } from 'react';
import Link from 'next/link';
import { GetStaticProps } from 'next';
import {
  TournamentCategoryItemFragment,
  TournamentListItemFragment,
} from '@amf/shared/types/graphql';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getTournaments, LIMIT } from '@amf/shared/data/Tournament';
import { getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import Button from '@amf/shared/components/button/Button';
import Loader from '@amf/shared/components/utils/Loader';
import Container from '@amf/shared/components/layout/Container';

import Layout from 'components/layout/Layout';
import TournamentFilter, {
  defaultValues,
  TournamentFilterData,
} from 'components/tournament/TournamentFilter';
import TournamentCard from 'components/tournament/TournamentCard';
import TournamentEmpty from 'components/tournament/TournamentEmpty';
import { AppLinks } from 'utils/links';
import TournamentListMap from 'components/tournament/TournamentListMap';
import Meta from 'components/Meta';

const apiKey = `/api/tournament`;
const getCacheKey =
  ({ category, region, query, dateFrom, dateTo }: TournamentFilterData) =>
  (pageIndex: number, prevPageData: TournamentListItemFragment[] | null) => {
    const params = new URLSearchParams({ offset: `${LIMIT * pageIndex}` });

    if (category) {
      params.append('category', category);
    }

    if (region) {
      params.append('region', region);
    }

    if (query) {
      params.append('query', query);
    }

    if (dateFrom) {
      params.append('dateFrom', dateFrom);
    }

    if (dateTo) {
      params.append('dateTo', dateTo);
    }

    if (prevPageData === null || pageIndex === 0) {
      return `${apiKey}?${params.toString()}`;
    }

    if (prevPageData.length < LIMIT) {
      return null;
    }

    return `${apiKey}/?${params.toString()}`;
  };

interface TournamentListPageProps extends NavigationProps {
  tournaments: TournamentListItemFragment[];
  categories: TournamentCategoryItemFragment[];
}

export default function TournamentListPage({
  tournaments: initialData,
  categories,
  navigation,
  partners,
}: TournamentListPageProps) {
  const { t } = useTranslate('malyfotbal');
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(true);
  const [filter, setFilter] = useState<TournamentFilterData>(defaultValues);
  const { data, size, setSize } = useSWRInfinite<TournamentListItemFragment[]>(
    getCacheKey(filter),
    { initialData: [initialData] },
  );

  const tournaments: TournamentListItemFragment[] = data
    ? ([] as TournamentListItemFragment[]).concat(...data)
    : [];

  const updateFilter = useCallback(async (data: TournamentFilterData) => {
    setFilter(data);
    setLoading(true);
    await setSize(1);
    setLoading(false);
  }, []);

  const renderLoadMore = () => {
    if (!tournaments || data === undefined) {
      return null;
    }

    if (size > data.length) {
      return (
        <Button type='button' variant='blue-outline' disabled>
          {t('tournaments.list.more.loading')}
        </Button>
      );
    }

    if (LIMIT * size > tournaments.length) {
      return null;
    }

    return (
      <Button type='button' variant='blue-outline' onClick={() => setSize(size + 1)}>
        {t('tournaments.list.more')}
      </Button>
    );
  };

  return (
    <div className='TournamentListPage'>
      <Layout navigation={navigation} partners={partners}>
        <Meta title={t('tournaments.list.title')} />
        <TournamentFilter
          title={<h1>{t('tournaments.list.title')}</h1>}
          button={
            <>
              <Button variant='blue-outline' onClick={() => setList(!list)}>
                {list && t('tournaments.list.map')}
                {!list && t('tournaments.list.list')}
              </Button>
              <Link href={AppLinks.tournamentNew}>
                <a>
                  <Button variant='blue'>{t('tournaments.list.add')}</Button>
                </a>
              </Link>
            </>
          }
          categories={categories}
          onUpdate={updateFilter}
        >
          {loading && <Loader>{t('tournaments.list.more.loading')}</Loader>}
          {!loading && tournaments.length === 0 && (
            <TournamentEmpty>{t('tournaments.list.empty')}</TournamentEmpty>
          )}
          {!loading && tournaments.length > 0 && (
            <Container position='center'>
              {list &&
                tournaments.map(tournament => (
                  <TournamentCard key={tournament.id} tournament={tournament} />
                ))}
              {!list && (
                <div className='TournamentListPage__map'>
                  <TournamentListMap tournaments={tournaments} />
                </div>
              )}
              {renderLoadMore()}
            </Container>
          )}
        </TournamentFilter>
      </Layout>
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const { categories, navigation } = await sdk.TournamentListPage({
    union: process.env.NEXT_PUBLIC_UNION,
    locale: getLanguageLocale(locale!),
  });

  return {
    props: {
      navigation,
      tournaments: await getTournaments(0, null, null, null, null, null),
      categories,
      partners: await getPartners(),
    },
    revalidate: 10,
  };
};
